import { createContext, useContext } from "react";
import { useSession } from "../auth/useSession";

/**
 * VISITOR, OR ORGANISER — available to the screens themselves.
 *
 * The shell has always known which mode it is in; the screens did not, and one of
 * them needs to. A screen that has nothing to show a PARTICIPANT — a confirmation
 * with no registration behind it, tomorrow a badge with no badge — must send them
 * somewhere useful. An ORGANISER reaches the very same screen ON PURPOSE, from the
 * panel's screen picker, precisely to configure what it says. Bouncing them would
 * make it the one screen they cannot set up.
 *
 * Defaults to `visitor`: the safer answer for anything rendered outside the shell,
 * since it withholds rather than reveals.
 */
export type Mode = "visitor" | "organiser";

const Context = createContext<Mode>("visitor");

export const ModeProvider = Context.Provider;

export function useMode(): Mode {
  return useContext(Context);
}

/**
 * ═══ L'ORGANISATEUR REGARDE SON SITE COMME UN VISITEUR NON CONNECTÉ ═══
 *
 * « Comment on pourrait basculer dans un mode non connecté mais en restant connecté en
 * mode édition » (2026-08-20). Depuis qu'une page peut être publique, l'organisateur a
 * besoin de voir ce que voit quelqu'un qui n'a pas de compte : quelles entrées de menu,
 * quelles tuiles, et où mène une page qu'il n'a pas ouverte. Sans ça, la seule mesure
 * était une fenêtre privée — donc sans le panneau, donc sans pouvoir corriger.
 *
 * ⚠️ C'EST UN APERÇU, PAS UNE DÉCONNEXION, et il faut le dire là où quelqu'un le lira.
 * Les requêtes partent toujours avec le jeton de l'organisateur : tout ce que le SERVEUR
 * filtre selon la session continue de répondre. L'aperçu est fidèle pour ce que les
 * RÉGLAGES commandent — le menu, les tuiles, la garde du châssis — et pour rien d'autre.
 * La seule vraie mesure reste une fenêtre privée.
 *
 * ⚠️ SÉPARÉ DE `Mode` PLUTÔT QUE FONDU DEDANS. Un troisième état « organisateur qui
 * regarde en visiteur » aurait obligé chaque lecteur de `useMode()` à choisir son camp,
 * et la question n'est pas la même : `mode` dit QUI PILOTE, celui-ci dit POUR QUI ON
 * DESSINE.
 *
 * Faux par défaut : hors de la barre, on dessine pour la personne qui est là.
 */
const Anonymous = createContext(false);

export const AnonymousProvider = Anonymous.Provider;

/** L'organisateur a-t-il demandé à voir son site en visiteur non connecté ? */
export function useAnonymousPreview(): boolean {
  return useContext(Anonymous);
}

/**
 * ═══ POUR QUI DESSINE-T-ON : QUELQU'UN DE CONNECTÉ, OU PAS ? ═══
 *
 * LA question, et elle a trois entrées : le mode (qui pilote), la session (y a-t-il
 * quelqu'un), l'aperçu (l'organisateur a-t-il demandé à voir en visiteur). Écrite ici
 * une fois plutôt qu'à ses deux endroits de lecture — le filtre du menu et la garde du
 * châssis —, parce que deux copies de cette règle, c'est un menu qui montre ce que la
 * garde refuse.
 *
 * ⚠️ L'ORGANISATEUR EST « CONNECTÉ » MÊME S'IL NE L'EST PAS. Il configure : lui refuser
 * ses propres écrans rendrait le site impossible à régler, et c'est déjà la règle du
 * châssis. L'aperçu est la seule chose qui le fasse changer d'avis.
 */
export function readerSignedIn(
  mode: Mode,
  signedIn: boolean,
  anonymousPreview: boolean,
): boolean {
  if (anonymousPreview) return false;
  return mode === "visitor" ? signedIn : true;
}

/**
 * La même règle, prise au contexte — pour tout ce qui est SOUS le châssis.
 *
 * ⚠️ LE CHÂSSIS, LUI, NE PEUT PAS L'UTILISER : c'est lui qui POSE le fournisseur, donc
 * le crochet y lirait le défaut et l'aperçu n'aurait aucun effet sur sa propre garde.
 * Il appelle la fonction pure avec son état à lui. D'où les deux, et une seule règle.
 */
export function useReaderSignedIn(): boolean {
  return readerSignedIn(useMode(), useSession(), useAnonymousPreview());
}
